import React, { useState, useEffect } from 'react';
import { Activity, Server, Users, Clock, AlertTriangle, CheckCircle } from 'lucide-react';

interface ServerStats {
  status: string;
  timestamp: string;
  version: string;
  deploymentCheck: string;
}

interface AnalyticsData {
  totalUsers: number;
  activeUsers: number;
  totalPosts: number;
  totalCrews: number;
  responseTime: number;
  errorRate: number;
}

const MonitorScreen = () => {
  const [serverStats, setServerStats] = useState<ServerStats | null>(null);
  const [analyticsData, setAnalyticsData] = useState<AnalyticsData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState<string>('--:--:--');
  const [logs, setLogs] = useState<string[]>([]);

  const addLog = (message: string) => {
    const timestamp = new Date().toLocaleTimeString();
    setLogs(prev => [`${timestamp}: ${message}`, ...prev.slice(0, 9)]);
  };

  const updateServerStatus = async () => {
    try {
      setIsLoading(true);
      
      // 서버 상태 확인
      const healthResponse = await fetch('https://e2b-backend.onrender.com/health');
      const healthData = await healthResponse.json();
      setServerStats(healthData);

      // 통계 정보 가져오기
      const statsResponse = await fetch('https://e2b-backend.onrender.com/api/stats');
      const statsData = await statsResponse.json();

      // 실시간 분석 데이터
      try {
        const analyticsResponse = await fetch('https://e2b-backend.onrender.com/api/analytics/stats/realtime');
        const analytics = await analyticsResponse.json();
        setAnalyticsData(analytics);
      } catch (analyticsError) {
        console.warn('분석 API 실패, 기본 통계 사용:', analyticsError);
        setAnalyticsData(statsData);
      }

      setLastUpdate(new Date().toLocaleTimeString());
      addLog(`서버 상태 확인: ${healthData.status}`);
      
    } catch (error) {
      console.error('서버 상태 확인 실패:', error);
      addLog(`서버 상태 확인 실패: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    updateServerStatus();
    const interval = setInterval(updateServerStatus, 30000); // 30초마다 업데이트
    return () => clearInterval(interval);
  }, []);

  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'ok':
      case 'healthy':
        return 'text-green-600 bg-green-100';
      case 'degraded':
        return 'text-yellow-600 bg-yellow-100';
      default:
        return 'text-red-600 bg-red-100';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'ok':
      case 'healthy':
        return <CheckCircle className="w-5 h-5" />;
      case 'degraded':
        return <AlertTriangle className="w-5 h-5" />;
      default:
        return <AlertTriangle className="w-5 h-5" />;
    }
  };

  return (
    <div className="space-y-6">
      {/* 헤더 */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-slate-800">시스템 모니터링</h2>
            <p className="text-slate-600 mt-1">실시간 서버 상태 및 성능 모니터링</p>
          </div>
          <div className="text-right">
            <div className="text-sm text-slate-500">마지막 업데이트</div>
            <div className="text-lg font-semibold text-slate-800">{lastUpdate}</div>
          </div>
        </div>
      </div>

      {/* 서버 상태 카드들 */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* 전체 상태 */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <div className="flex items-center">
            <div className={`w-4 h-4 rounded-full mr-3 ${getStatusColor(serverStats?.status || 'unknown')}`}></div>
            <div>
              <div className="text-sm font-medium text-slate-500">전체 상태</div>
              <div className="text-xl font-bold text-slate-800 flex items-center gap-2">
                {getStatusIcon(serverStats?.status || 'unknown')}
                {serverStats?.status || 'UNKNOWN'}
              </div>
            </div>
          </div>
        </div>

        {/* 응답 시간 */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <div className="flex items-center">
            <Clock className="w-5 h-5 text-blue-600 mr-3" />
            <div>
              <div className="text-sm font-medium text-slate-500">응답 시간</div>
              <div className="text-xl font-bold text-slate-800">
                {analyticsData?.responseTime ? `${analyticsData.responseTime}ms` : '--'}
              </div>
            </div>
          </div>
        </div>

        {/* 활성 사용자 */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <div className="flex items-center">
            <Users className="w-5 h-5 text-green-600 mr-3" />
            <div>
              <div className="text-sm font-medium text-slate-500">활성 사용자</div>
              <div className="text-xl font-bold text-slate-800">
                {analyticsData?.activeUsers || '--'}
              </div>
            </div>
          </div>
        </div>

        {/* 에러율 */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <div className="flex items-center">
            <Activity className="w-5 h-5 text-red-600 mr-3" />
            <div>
              <div className="text-sm font-medium text-slate-500">에러율</div>
              <div className="text-xl font-bold text-slate-800">
                {analyticsData?.errorRate ? `${analyticsData.errorRate}%` : '--'}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 상세 정보 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* 서버 정보 */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h3 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
            <Server className="w-5 h-5" />
            서버 정보
          </h3>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-slate-600">버전:</span>
              <span className="font-medium">{serverStats?.version || '--'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-600">배포 체크:</span>
              <span className="font-medium">{serverStats?.deploymentCheck || '--'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-600">타임스탬프:</span>
              <span className="font-medium text-sm">{serverStats?.timestamp || '--'}</span>
            </div>
          </div>
        </div>

        {/* 통계 정보 */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h3 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
            <Activity className="w-5 h-5" />
            통계 정보
          </h3>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-slate-600">총 사용자:</span>
              <span className="font-medium">{analyticsData?.totalUsers || '--'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-600">총 게시물:</span>
              <span className="font-medium">{analyticsData?.totalPosts || '--'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-600">총 크루:</span>
              <span className="font-medium">{analyticsData?.totalCrews || '--'}</span>
            </div>
          </div>
        </div>
      </div>

      {/* 로그 */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <h3 className="text-lg font-semibold text-slate-800 mb-4">시스템 로그</h3>
        <div className="bg-slate-50 rounded-lg p-4 max-h-48 overflow-y-auto">
          {logs.length === 0 ? (
            <div className="text-slate-500 text-center py-4">로그가 없습니다.</div>
          ) : (
            <div className="space-y-1">
              {logs.map((log, index) => (
                <div key={index} className="text-sm font-mono text-slate-700">
                  {log}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* 새로고침 버튼 */}
      <div className="flex justify-center">
        <button
          onClick={updateServerStatus}
          disabled={isLoading}
          className="bg-blue-500 hover:bg-blue-600 disabled:bg-blue-300 text-white px-6 py-3 rounded-lg font-medium transition-colors flex items-center gap-2"
        >
          {isLoading ? (
            <>
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              업데이트 중...
            </>
          ) : (
            <>
              <Activity className="w-4 h-4" />
              새로고침
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default MonitorScreen;
